import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { DataTable } from '@/components/DataTable';
import { FormControl, type FormField } from '@/components/CrudPage';
import {
  Badge,
  Button,
  ConfirmDialog,
  Modal,
  PageHeader,
  Toast,
  Toggle,
  IconAction,
} from '@/components/ui';
import { IconClock, IconPencil, IconRefresh, IconTrash, IconX } from '@/components/icons';
import { formatDateTime, formatNumber } from '@/lib/format';

interface Client {
  id: string;
  name: string;
  slug: string;
  status: string;
  billing_email: string | null;
  price_multiplier: string;
  expose_provider_costs: boolean;
  max_concurrent_calls: number;
  plan_name: string | null;
  bot_count: number;
  user_count: number;
  vicidial_user_count: number;
  calls_30d: number;
  created_at: string;
}

const STATUS_TONES: Record<string, 'green' | 'amber' | 'red' | 'gray'> = {
  ACTIVE: 'green',
  TRIAL: 'amber',
  SUSPENDED: 'red',
  CANCELLED: 'gray',
};

const FIELDS: FormField[] = [
  { name: 'name', label: 'Company name', required: true, placeholder: 'ABC Home Services' },
  {
    name: 'slug',
    label: 'Slug',
    required: true,
    placeholder: 'abc-home-services',
    hint: 'Lowercase letters, numbers and dashes. Used to identify the tenant.',
  },
  {
    name: 'status',
    label: 'Status',
    type: 'select',
    defaultValue: 'TRIAL',
    options: ['TRIAL', 'ACTIVE', 'SUSPENDED', 'CANCELLED'].map((value) => ({
      value,
      label: value,
    })),
  },
  { name: 'billing_email', label: 'Billing email', type: 'email' },
  { name: 'contact_name', label: 'Contact name' },
  { name: 'contact_phone', label: 'Contact phone' },
  {
    name: 'price_multiplier',
    label: 'Price multiplier',
    type: 'number',
    defaultValue: 2.2,
    hint: 'CLIENT PRICE = ACTUAL COST × this. Default 2.2.',
  },
  {
    name: 'max_concurrent_calls',
    label: 'Max concurrent calls',
    type: 'number',
    defaultValue: 50,
  },
  {
    name: 'max_bots',
    label: 'Bot allowance',
    type: 'number',
    defaultValue: 10,
    hint: 'Enforced by the database, not just this form.',
  },
  {
    name: 'max_vicidial_servers',
    label: 'Vicidial server allowance',
    type: 'number',
    defaultValue: 5,
    hint: 'How many dialers this client may configure.',
  },
  {
    name: 'max_vicidial_users',
    label: 'Vicidial agent allowance',
    type: 'number',
    defaultValue: 50,
    hint: 'Across all of their servers.',
  },
  {
    name: 'expose_provider_costs',
    label: 'Show provider costs to this client',
    type: 'checkbox',
    hint: 'Off by default — the markup should not be inferable from the client portal.',
  },
  {
    name: 'admin_email',
    label: 'First admin email',
    type: 'email',
    hint: 'Optional — creates the client admin in the same step.',
  },
  { name: 'admin_name', label: 'First admin name' },
  {
    name: 'admin_password',
    label: 'First admin password',
    type: 'password',
    hint: 'At least 12 characters. Required if an admin email is given.',
  },
];

export default function Clients() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Record<string, string | number | boolean>>({
    status: 'TRIAL',
    price_multiplier: 2.2,
    max_concurrent_calls: 50,
    expose_provider_costs: false,
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);

  const [cancelling, setCancelling] = useState<{ id: string; name: string } | null>(
    null,
  );

  const [suspending, setSuspending] = useState<{ id: string; name: string; to: string } | null>(
    null,
  );
  const [purging, setPurging] = useState<{ id: string; name: string } | null>(null);
  const [voidUnpaid, setVoidUnpaid] = useState(false);

  // Suspend and resume are the same call with a different status: it is one
  // field on the account, and pretending otherwise would be two endpoints
  // that can disagree.
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      api.patch(`/admin/clients/${id}`, { status }),
    onSuccess: (_r, vars) => {
      setSuspending(null);
      queryClient.invalidateQueries({ queryKey: ['admin-clients'] });
      setToast({
        message:
          vars.status === 'SUSPENDED'
            ? 'Suspended. Their bots stop taking calls.'
            : 'Back on. Their bots take calls again.',
        tone: 'good',
      });
    },
    onError: (err) => {
      setSuspending(null);
      setToast({ message: errorMessage(err, 'Could not change it'), tone: 'bad' });
    },
  });

  const purgePreview = useQuery({
    queryKey: ['client-purge-preview', purging?.id],
    queryFn: () =>
      api.get<{
        destroys: Record<string, number>;
        unpaid: number;
        blockers: string[];
        can_purge: boolean;
        warning: string;
        dnc_note: string;
      }>(`/admin/clients/${purging?.id}/purge-preview`),
    enabled: !!purging,
  });

  const purge = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.post<{ name: string; asterisk: Array<{ code: string; removed: boolean; detail: string }> }>(
        `/admin/clients/${id}/purge`,
        { confirm_name: name, void_unpaid: voidUnpaid },
      ),
    onSuccess: (result) => {
      setPurging(null);
      queryClient.invalidateQueries({ queryKey: ['admin-clients'] });
      const failed = (result.asterisk ?? []).filter((a) => !a.removed);
      const trunks = (result.asterisk ?? []).length;
      setToast({
        message: failed.length
          ? `${result.name} deleted, but ${failed.map((f) => f.code).join(', ')} could not be removed from Asterisk — run: sudo glee-asterisk-apply remove ${failed[0].code}`
          : `${result.name} deleted${trunks ? `, with ${trunks} Vicidial trunk${trunks === 1 ? '' : 's'} and dialplan removed from Asterisk` : ''}.`,
        tone: failed.length ? 'bad' : 'good',
      });
    },
    onError: (err) =>
      setToast({ message: errorMessage(err, 'Could not delete it'), tone: 'bad' }),
  });

  const cancel = useMutation({
    mutationFn: (id: string) =>
      api.delete<{ name: string; bots_archived: number }>(`/admin/clients/${id}`),
    onSuccess: (result) => {
      setCancelling(null);
      queryClient.invalidateQueries({ queryKey: ['admin-clients'] });
      setToast({
        message: `${result.name} cancelled. ${result.bots_archived} bot${
          result.bots_archived === 1 ? '' : 's'
        } archived.`,
        tone: 'good',
      });
    },
    onError: (err) => {
      setCancelling(null);
      setToast({ message: errorMessage(err, 'Could not cancel it'), tone: 'bad' });
    },
  });

  const create = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {};
      for (const field of FIELDS) {
        const value = values[field.name];
        if (value === '' || value === undefined) continue;
        payload[field.name] = field.type === 'number' ? Number(value) : value;
      }
      return api.post<{ client: Client }>('/admin/clients', payload);
    },
    onSuccess: (result) => {
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ['admin-clients'] });
      setToast({ message: `${result.client.name} created`, tone: 'good' });
    },
    onError: (err) =>
      setFormError(errorMessage(err, 'Could not create client')),
  });

  return (
    <div className="w-full">
      <PageHeader
        title="Clients"
        description="Every tenant on the platform. Each is fully isolated from the others."
        actions={<Button onClick={() => setOpen(true)}>New client</Button>}
      />

      <DataTable<Client>
        queryKey="admin-clients"
        path="/admin/clients"
        searchPlaceholder="Search clients…"
        onRowClick={(client) => navigate(`/clients/${client.id}`)}
        emptyTitle="No clients yet"
        emptyDescription="Create the first tenant to get started."
        emptyAction={<Button onClick={() => setOpen(true)}>New client</Button>}
        filters={[
          {
            key: 'status',
            label: 'Status',
            options: Object.keys(STATUS_TONES).map((value) => ({ value, label: value })),
          },
        ]}
        columns={[
          {
            key: 'name',
            header: 'Client',
            render: (client) => (
              <div>
                <div className="font-medium text-ink-900 dark:text-ink-50">{client.name}</div>
                <div className="font-mono text-xs text-ink-500">{client.slug}</div>
              </div>
            ),
          },
          {
            key: 'status',
            header: 'Status',
            render: (client) => (
              <Badge tone={STATUS_TONES[client.status] ?? 'gray'}>{client.status}</Badge>
            ),
          },
          {
            key: 'plan',
            header: 'Plan',
            render: (client) => client.plan_name ?? <span className="text-ink-400">—</span>,
          },
          {
            key: 'multiplier',
            header: 'Multiplier',
            numeric: true,
            render: (client) => `${Number(client.price_multiplier).toFixed(2)}×`,
          },
          {
            key: 'bots',
            header: 'Bots',
            numeric: true,
            render: (client) => formatNumber(client.bot_count),
          },
          {
            key: 'vicidial_users',
            header: 'Agents',
            numeric: true,
            render: (client) => formatNumber(client.vicidial_user_count),
          },
          {
            key: 'calls',
            header: 'Calls (30d)',
            numeric: true,
            render: (client) => formatNumber(client.calls_30d),
          },
          {
            key: 'costs',
            header: 'Cost visibility',
            render: (client) =>
              client.expose_provider_costs ? (
                <Badge tone="amber">Exposed</Badge>
              ) : (
                <span className="text-xs text-ink-400">Hidden</span>
              ),
          },
          {
            key: 'created_at',
            header: 'Created',
            render: (client) => (
              <span className="text-xs text-ink-500">{formatDateTime(client.created_at)}</span>
            ),
          },
          {
            key: '__actions',
            header: '',
            render: (client) => (
              // Stops the row click through to the client detail page.
              <div onClick={(event) => event.stopPropagation()} className="flex justify-end gap-1">
                <IconAction label="Open" onClick={() => navigate(`/clients/${client.id}`)}>
                  <IconPencil />
                </IconAction>
                {client.status === 'CANCELLED' ? (
                  <IconAction
                    label="Restore"
                    busy={setStatus.isPending && setStatus.variables?.id === client.id}
                    onClick={() => setStatus.mutate({ id: client.id, status: 'ACTIVE' })}
                  >
                    <IconRefresh />
                  </IconAction>
                ) : (
                  <>
                    {/* Suspend is the non-payment button and the one that
                        should be reached for first: it is one field, it stops
                        the bill, and turning it back on is one click. */}
                    <IconAction
                      label={client.status === 'SUSPENDED' ? 'Resume' : 'Suspend'}
                      busy={setStatus.isPending && setStatus.variables?.id === client.id}
                      onClick={() =>
                        client.status === 'SUSPENDED'
                          ? setStatus.mutate({ id: client.id, status: 'ACTIVE' })
                          : setSuspending({ id: client.id, name: client.name, to: 'SUSPENDED' })
                      }
                    >
                      {client.status === 'SUSPENDED' ? <IconRefresh /> : <IconClock />}
                    </IconAction>
                    <IconAction label="Cancel account" onClick={() => setCancelling({ id: client.id, name: client.name })}>
                      <IconX />
                    </IconAction>
                  </>
                )}
                <IconAction
                  label="Delete permanently"
                  tone="danger"
                  onClick={() => {
                    setVoidUnpaid(false);
                    setPurging({ id: client.id, name: client.name });
                  }}
                >
                  <IconTrash />
                </IconAction>
              </div>
            ),
          },
        ]}
      />

      <Modal
        open={open}
        wide
        onClose={() => setOpen(false)}
        title="New client"
        description="Creates the tenant, its default pricing rule and, optionally, its first admin."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={create.isPending}
              onClick={() => {
                setFormError(null);
                if (!values.name || !values.slug) {
                  setFormError('Company name and slug are required');
                  return;
                }
                if (values.admin_email && !values.admin_password) {
                  setFormError('An admin password is required when an admin email is given');
                  return;
                }
                // Mirror the server's minimum here. Letting the request go and
                // showing whatever came back left the operator reading
                // "Request validation failed" with no idea which field.
                const password = String(values.admin_password ?? '');
                if (password && password.length < 12) {
                  setFormError(
                    `The admin password must be at least 12 characters (this one is ${password.length}).`,
                  );
                  return;
                }
                if (password && password.length > 72) {
                  setFormError('The admin password must be 72 characters or fewer.');
                  return;
                }
                create.mutate();
              }}
            >
              Create client
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {formError && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-950/50 dark:text-red-300 dark:ring-red-900">
              {formError}
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            {FIELDS.map((field) => (
              <div
                key={field.name}
                className={field.type === 'checkbox' ? 'sm:col-span-2' : undefined}
              >
                <FormControl
                  field={field}
                  value={values[field.name]}
                  onChange={(next) =>
                    setValues((prev) => ({ ...prev, [field.name]: next }))
                  }
                />
              </div>
            ))}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!suspending}
        title={`Suspend ${suspending?.name ?? ''}?`}
        confirmLabel="Suspend"
        busy={setStatus.isPending}
        onCancel={() => setSuspending(null)}
        onConfirm={() =>
          suspending && setStatus.mutate({ id: suspending.id, status: 'SUSPENDED' })
        }
        body={
          <div className="space-y-2 text-sm">
            <p>
              Their bots stop taking calls immediately and nobody on the
              account can sign in. Nothing is deleted and nothing is archived.
            </p>
            <p className="text-xs text-ink-500">
              One click to put it back. This is the non-payment button —
              cancelling is for ending the relationship, and deleting is for
              when the record itself has to go.
            </p>
            <p className="text-xs text-ink-500">
              Their dialer keeps running its own campaigns; this only stops
              the bill on our side.
            </p>
          </div>
        }
      />

      <ConfirmDialog
        open={!!purging}
        title={`Permanently delete ${purging?.name ?? ''}?`}
        confirmLabel="Delete for ever"
        requireTyping={purging?.name}
        busy={purge.isPending}
        onCancel={() => {
          setPurging(null);
        }}
        onConfirm={() =>
          purging && purge.mutate({ id: purging.id, name: purging.name })
        }
        body={
          <div className="space-y-2 text-sm">
            {purgePreview.isPending ? (
              <p className="text-xs text-ink-500">Counting what would go…</p>
            ) : purgePreview.data ? (
              <>
                <p className="text-red-700 dark:text-red-400">
                  {purgePreview.data.warning}
                </p>
                {/* The counts, because "everything" is not a quantity and a
                    confirmation that does not say what it destroys is a
                    button rather than a decision. */}
                <ul className="grid grid-cols-2 gap-x-4 text-xs text-ink-600 dark:text-ink-300">
                  {Object.entries(purgePreview.data.destroys).map(([what, n]) => (
                    <li key={what} className="flex justify-between">
                      <span>{what.replace(/_/g, ' ')}</span>
                      <strong className="tabular-nums">{n.toLocaleString()}</strong>
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-ink-500">{purgePreview.data.dnc_note}</p>
                {(purgePreview.data.destroys.vicidial_servers ?? 0) > 0 && (
                  <p className="text-xs text-ink-600 dark:text-ink-300">
                    Each Vicidial server's SIP trunk and dialplan is removed from
                    Asterisk too, so the dialer can no longer send this client calls.
                  </p>
                )}
                {purgePreview.data.unpaid > 0 && (
                  <div className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                    <p className="mb-2">{purgePreview.data.blockers[0]}</p>
                    <Toggle
                      label={`Void the ${purgePreview.data.unpaid} unpaid invoice${purgePreview.data.unpaid === 1 ? '' : 's'} and delete anyway`}
                      checked={voidUnpaid}
                      onChange={setVoidUnpaid}
                    />
                  </div>
                )}
              </>
            ) : null}
          </div>
        }
      />

      <ConfirmDialog
        open={!!cancelling}
        title={`Cancel ${cancelling?.name ?? ''}?`}
        confirmLabel="Cancel the account"
        requireTyping={cancelling?.name}
        busy={cancel.isPending}
        onCancel={() => setCancelling(null)}
        onConfirm={() => cancelling && cancel.mutate(cancelling.id)}
        body={
          <div className="space-y-2 text-sm">
            <p>
              The account is marked <strong>cancelled</strong> and every one of
              its bots is archived, so the dialer stops being handed calls for
              it.
            </p>
            <p className="text-xs text-ink-500">
              Nothing is erased. Calls, transcripts, recordings and invoices
              stay exactly where they are — they are the record of what was
              said to real people and what they were charged, and a dispute
              six months from now needs them.
            </p>
            <p className="text-xs text-ink-500">
              To restart the account, set its status back to ACTIVE. The bots
              have to be un-archived one at a time.
            </p>
          </div>
        }
      />

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}
